/**
 * Applies syntax highlighting to code blocks using highlight.js.
 */

import { BaseEnricher } from './BaseEnricher.js';
import type { EnricherConfig, EnricherContext } from './types.js';

export class SyntaxHighlightEnricher extends BaseEnricher {
  private static readonly LANGUAGE_ALIASES: Record<string, string> = {
    ps: 'powershell',
    ps1: 'powershell',
    pwsh: 'powershell',
    shell: 'bash',
    zsh: 'bash',
    yml: 'yaml',
    md: 'markdown',
    ts: 'typescript',
    js: 'javascript',
    py: 'python',
    rb: 'ruby',
    cs: 'csharp',
    csharp: 'csharp',
  };

  constructor(config: EnricherConfig = {}) {
    super({ priority: 100, ...config });
  }

  async process(container: HTMLElement, _context: EnricherContext): Promise<void> {
    if (!window.hljs) {
      console.warn('SyntaxHighlightEnricher: highlight.js not loaded');
      return;
    }

    container.querySelectorAll('pre').forEach(pre => {
      const code = pre.querySelector('code');
      if (!code) return;

      this.highlightCode(code as HTMLElement);
    });
  }

  /**
   * Apply syntax highlighting to a code element.
   */
  highlightCode(code: HTMLElement): void {
    if (!window.hljs) return;

    // Get language from class (e.g., "language-bash" -> "bash")
    const langClass = Array.from(code.classList).find(c => c.startsWith('language-'));

    if (langClass) {
      const rawLang = langClass.replace('language-', '').toLowerCase();
      const lang = SyntaxHighlightEnricher.LANGUAGE_ALIASES[rawLang] || rawLang;

      // Use explicit language when supported; otherwise fall back to auto-detection.
      if (window.hljs.getLanguage(lang)) {
        if (lang !== rawLang) {
          code.classList.remove(langClass);
          code.classList.add(`language-${lang}`);
        }
        window.hljs.highlightElement(code);
        return;
      }

      // Unknown fenced language: remove explicit class so hljs can auto-detect.
      code.classList.remove(langClass);
      window.hljs.highlightElement(code);
    } else {
      // Auto-detect language
      window.hljs.highlightElement(code);
    }
  }

  /**
   * Re-apply highlighting after content change.
   * Called by other enrichers (e.g., VariableEnricher) after modifying code content.
   */
  rehighlight(code: HTMLElement): void {
    if (!window.hljs) return;

    // Clear existing highlighting
    code.classList.remove('hljs');
    code.removeAttribute('data-highlighted');

    this.highlightCode(code);
  }
}

// Export a singleton instance for re-highlighting from other enrichers
export const syntaxHighlighter = new SyntaxHighlightEnricher();
